import { EnumSelect } from "@app/components/poke/plugins/EnumSelect";
import { ServerSideSearchEnumSelect } from "@app/components/poke/plugins/ServerSideSearchEnumSelect";
import {
  PokeForm,
  PokeFormControl,
  PokeFormDescription,
  PokeFormField,
  PokeFormInput,
  PokeFormItem,
  PokeFormLabel,
  PokeFormMessage,
  PokeFormTextArea,
  PokeFormUpload,
} from "@app/components/poke/shadcn/ui/form";
import type { PokeGetPluginDetailsResponseBody } from "@app/types/api/poke/plugins/manifest";
import type {
  AsyncEnumValues,
  EnumValues,
  PluginResourceTarget,
} from "@app/types/poke/plugins";
import { createZodSchemaFromArgs } from "@app/types/poke/plugins";
import { Button, Checkbox, SliderToggle } from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
// biome-ignore lint/correctness/noUnusedImports: ignored using `--suppress`
import React, { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import type { z } from "zod";

type FallbackArgs = Record<string, unknown>;

type FormValues<T> = T extends z.ZodTypeAny ? z.infer<T> : FallbackArgs;

interface PluginFormProps {
  asyncArgs?: Partial<
    Record<string, string | number | boolean | AsyncEnumValues | EnumValues>
  > | null;
  disabled?: boolean;
  manifest: PokeGetPluginDetailsResponseBody["manifest"];
  onSubmit: (args: FormValues<any>) => Promise<void>;
  pluginResourceTarget: PluginResourceTarget;
}

export function PluginForm({
  asyncArgs,
  disabled,
  manifest,
  onSubmit,
  pluginResourceTarget,
}: PluginFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const argsSchema = useMemo(() => {
    if (!manifest) {
      return null;
    }

    // Create schema from original manifest - async values are handled in rendering
    return createZodSchemaFromArgs(manifest.args);
  }, [manifest]);

  const defaultValues = useMemo(() => {
    if (!manifest) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(manifest.args).map(([key, arg]) => {
        switch (arg.type) {
          case "text":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as string)
                : "",
            ];
          case "string":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as string)
                : "",
            ];
          case "number":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as number)
                : (arg.default ?? 0),
            ];
          case "boolean":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as boolean)
                : (arg.default ?? false),
            ];
          case "enum":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as AsyncEnumValues)
                    .filter((v) => v.checked)
                    .map((v) => v.value)
                : arg.values.filter((v) => v.checked).map((v) => v.value),
            ];
          case "date":
            return [
              key,
              arg.async && asyncArgs?.[key] !== undefined
                ? (asyncArgs[key] as string)
                : "",
            ];
          default:
            return [key, null];
        }
      })
    );
  }, [manifest, asyncArgs]);

  const form = useForm({
    resolver: argsSchema ? zodResolver(argsSchema) : undefined,
    defaultValues,
  });

  const dependencyFields = useMemo(() => {
    if (!manifest) {
      return [];
    }
    return [
      ...new Set(
        Object.values(manifest.args)
          .filter((arg) => arg.dependsOn)
          .flatMap((arg) =>
            Array.isArray(arg.dependsOn)
              ? arg.dependsOn.map((c) => c.field)
              : [arg.dependsOn!.field]
          )
      ),
    ];
  }, [manifest]);

  const watchedValuesArray = useWatch({
    control: form.control,
    name: dependencyFields,
  });

  const watchedValues: Record<string, unknown> = {};
  dependencyFields.forEach((field, index) => {
    watchedValues[field] = watchedValuesArray[index];
  });

  async function handleSubmit(values: FormValues<typeof argsSchema>) {
    // Lock the form to prevent multiple submissions.
    setIsSubmitted(true);

    await onSubmit(values);
  }

  if (!manifest) {
    return null;
  }

  const workspaceId =
    "workspace" in pluginResourceTarget
      ? pluginResourceTarget.workspace.sId
      : null;

  return (
    <PokeForm {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="max-w-[600px] space-y-8"
      >
        <div className="max-h-[50vh] space-y-8 overflow-y-auto pr-2">
          {Object.entries(manifest.args).map(([key, arg]) => {
            const conditions = arg.dependsOn
              ? Array.isArray(arg.dependsOn)
                ? arg.dependsOn
                : [arg.dependsOn]
              : [];
            const isDependentFieldHidden = conditions.some((c) => {
              const watched = watchedValues[c.field];
              // Enum fields surface their selection as an array of values, so
              // match by membership; everything else compares by value.
              if (Array.isArray(watched)) {
                return !watched.includes(c.value);
              }
              return watched !== c.value;
            });

            if (isDependentFieldHidden) {
              return null;
            }

            let defaultValue = undefined;
            let options = undefined;

            if (arg.type === "enum") {
              options =
                arg.async && asyncArgs?.[key]
                  ? (asyncArgs[key] as AsyncEnumValues)
                  : arg.values;
              defaultValue = options
                .filter((v) => v.checked)
                .map((v) => v.value);
            }
            const fieldDescription =
              arg.asyncDescription &&
              asyncArgs?.[`${key}_description`] !== undefined
                ? String(asyncArgs[`${key}_description`])
                : arg.description;

            return (
              <PokeFormField
                key={key}
                control={form.control}
                name={key}
                disabled={disabled}
                defaultValue={defaultValue}
                render={({ field }) => (
                  <PokeFormItem>
                    <div
                      className={
                        arg.type === "boolean"
                          ? "flex flex-row items-center gap-x-2"
                          : "flex flex-col gap-y-2"
                      }
                    >
                      <PokeFormLabel>{arg.label}</PokeFormLabel>
                      <PokeFormControl>
                        <>
                          {arg.type === "text" && (
                            <PokeFormTextArea {...field} />
                          )}
                          {arg.type === "string" && (
                            <PokeFormInput {...field} />
                          )}
                          {arg.type === "number" && (
                            <PokeFormInput
                              type={arg.variant === "text" ? "text" : "number"}
                              {...field}
                              onChange={(e) => {
                                const parsed = Number(e.target.value);
                                if (isFinite(parsed)) {
                                  field.onChange(parsed);
                                }
                              }}
                            />
                          )}
                          {arg.type === "boolean" &&
                            arg.variant === "toggle" && (
                              <SliderToggle
                                selected={field.value}
                                onClick={() => field.onChange(!field.value)}
                                disabled={field.disabled}
                              />
                            )}
                          {arg.type === "boolean" &&
                            (arg.variant === "checkbox" ||
                              arg.variant === undefined) && (
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          {arg.type === "enum" &&
                            arg.serverSideSearch &&
                            workspaceId && (
                              <ServerSideSearchEnumSelect
                                label={arg.label}
                                onValuesChange={(values) =>
                                  field.onChange(values)
                                }
                                placeholder="Search by name or email"
                                staticOptions={options ?? arg.values}
                                values={field.value}
                                workspaceId={workspaceId}
                              />
                            )}
                          {arg.type === "enum" &&
                            !(arg.serverSideSearch && workspaceId) && (
                              <EnumSelect
                                label={arg.label}
                                onValuesChange={(values) =>
                                  field.onChange(values)
                                }
                                options={options ?? []}
                                placeholder="Select value"
                                values={field.value}
                                multiple={arg.multiple}
                              />
                            )}
                          {arg.type === "file" && (
                            <PokeFormUpload type="file" {...field} />
                          )}
                          {arg.type === "date" && (
                            <PokeFormInput type="date" {...field} />
                          )}
                        </>
                      </PokeFormControl>
                    </div>
                    {fieldDescription && (
                      <PokeFormDescription>
                        {fieldDescription}
                      </PokeFormDescription>
                    )}
                    <PokeFormMessage />
                  </PokeFormItem>
                )}
              />
            );
          })}
        </div>
        <Button
          type="submit"
          variant="outline"
          disabled={isSubmitted}
          label="Run"
        />
      </form>
    </PokeForm>
  );
}
